<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
// Vue
import { computed, watch } from "vue";
import { Plus, Truck } from "@lucide/vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";

// Data Providers
import { useTransportTypeOptions } from "@/dataProviders/TransportTypeOptions";
import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";

// Features
import LoadingWrapper from "@/components/Reusables/Loading/LoadingWrapper/LoadingWrapper.vue";
import StepIntro from "../StepIntro/StepIntro.vue";

// Types
import { WizardProjectType } from "@/features/BuildingProject";

// Components
import TransportRow from "./TransportRow.vue";
import { withDefaultRow } from "./defaultTransportRow";
import { Transport_Type_Enum } from "@/graphql";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import calculationTypeVariant from "@/services/enums/calculationTypeVariant";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

const props = defineProps<{
  wasValidated: boolean;
  project: WizardProjectType;
}>();

const calculationTypes = useCalculationTypes();
const transportTypeOptions = useTransportTypeOptions();
const { isExistingProject, projectId, field } = useWizardProject(() => props.project);

const transportTypes = field("transportTypes");
const projectWrapper = useProjectWrapper();
const revisionsStore = useProjectRevisionsStore();

// The rows of the revision being PREVIEWED, or null to show the live, editable list.
//
// Same shape and the same reasoning as OperationalEnergyUse.vue and Address.vue: gated on
// `isPreviewing` rather than on every pinned read, because this is a form and its rule is "what you
// see is what Save writes" — the overlay, the read-only state and the disabled Save button all hang
// off one condition. When an admin has merely parked the pointer on an older save point, the team
// keeps editing the live head here.
//
// Display-only, and structurally so: the overlay is a SEPARATE list, so nothing in this step can
// write a previewed row into `project.transportTypes`. That matters beyond the screen — `runSave`
// sends the transport rows when `payloadChanged(project.transportTypes, saved)`, so a row folded in
// here would be written to the working revision on the next save, and the frozen copies' ids are
// rejected by the update permission (RTLCA-5772).
const revisionTransports = computed(() =>
  projectId.value && revisionsStore.isPreviewing(projectId.value) ? revisionsStore.readTransportsFor(projectId.value) : null
);

/** True while the step shows a revision's saved rows instead of the live, editable ones. */
const isReadOnly = computed(() => revisionTransports.value !== null);

/** What is on screen: the previewed revision's own rows (already copies), else the live list. */
const displayedTransportTypes = computed(() => revisionTransports.value ?? transportTypes.value ?? []);

const BR2025 = computed(() => {
  const calculationTypeId = props.project.emissionCalculationAssumptions.calculationType?.calculationTypeEnum;

  if (!calculationTypeId) {
    return null;
  }

  const item = calculationTypes.getSelectedCalculationType(calculationTypeId);

  return item?.variant?.variant === calculationTypeVariant.BR182025;
});

const generateDefaultTransportType = () => {
  // TODO samme i transportType og så lad det slå igennem i transportRow
  const defaultType = transportTypeOptions.getDefaultTransportType();

  return {
    // Simulate id coming from backend for the already saved data. The row keeps this id once saved,
    // so the next read brings it back as the Default row and nothing is minted again.
    id: uuidv4(),

    // The name of this is always default, not to be confused with transport type label
    transport_type_name: "Default",

    // Set default values from TransportTypeOptionType
    distance: defaultType?.distance ?? null,
    emission: defaultType?.emission ?? null,

    // Transport option data. `isDefault` says this row IS the project's Default row — the one the save
    // points `project_transport_default` at — not that its option is the country's default one.
    transport: {
      id: defaultType?.id ?? "",
      isDefault: true,
      emission: defaultType?.emission ?? null,
      label: defaultType?.label ?? "",
      transportType: defaultType?.transportType ?? Transport_Type_Enum.Truck,

      // Indicator type data
      indicatorType: {
        id: defaultType?.indicatorType?.id ?? -1,
        label: defaultType?.indicatorType?.label ?? "",
        identifier: defaultType?.indicatorType?.identifier ?? "",
        unit: defaultType?.indicatorType?.unit ?? "",
      },
    },
    // Empty values not relevant for this situation
    project_id: "",
    tenant_id: "",
  };
};

const generateTransportType = () => {
  // Basically just an empty transport type but with an id so it exists in the list
  return {
    id: uuidv4(),
    transport_type_name: "",
    distance: null,
    emission: null,

    // Transport option data
    transport: {
      id: "",
      isDefault: false,
      emission: null,
      label: "",
      transportType: Transport_Type_Enum.Truck, // Enum needs to be there

      // Indicator type data
      indicatorType: {
        id: -1,
        label: "",
        identifier: "",
        unit: "",
      },
    },

    // Empty values not relevant for this situation
    project_id: "",
    tenant_id: "",
  };
};

const handleAddTransport = () => {
  if (isReadOnly.value) return;
  const newTransport = generateTransportType();
  transportTypes.value = [...(transportTypes.value || []), newTransport];
  projectWrapper.setAddedTransportTypeIds([...(projectWrapper.getAddedTransportTypeIds() || []), newTransport.id]);
};

const handleRemoveTransport = (id: string) => {
  if (isReadOnly.value) return;
  transportTypes.value = transportTypes.value?.filter((transportType) => transportType.id !== id) || [];
};

const allowDelete = (id: string) => {
  // A previewed revision's rows are history: nothing about them can be removed from here, and the
  // ids are that revision's own copies, so they are not in `addedTransportTypeIds` anyway.
  if (isReadOnly.value) return false;

  // If project has id then we are in edit and we can not delete anything else than allowDelete
  if (isExistingProject.value) {
    return projectWrapper.getAddedTransportTypeIds().includes(id);
  } else return true;
};

const genericTransportTypes = computed(() => {
  return displayedTransportTypes.value.filter((transportType) => !transportType.transport.isDefault);
});

const defaultTransportTypes = computed(() => {
  return displayedTransportTypes.value.filter((transportType) => transportType.transport.isDefault);
});

// The list always carries one Default row, synthesised from the country's default option once the
// options have loaded. ADDED to the list, never assigned over it: this used to set
// `transportTypes.value = [generateDefaultTransportType()]` whenever no Default was present, which
// dropped every generic row along with it (RTLCA-5772). Watched together with the list itself, so a
// list that arrives later or is swapped by a save or discard is completed the same way — and because
// `withDefaultRow` returns the same array when nothing is missing, the assignment never loops.
//
// Its sources are the LIVE list, never the displayed one: completing the working state is right
// whether or not a revision is on screen (the synthesised Default is a pending edit either way, and
// Save is refused while previewing), and reading the overlay here is what would let a frozen row be
// written back. A previewed revision with no rows is left empty — nothing is minted into it.
watch(
  [() => transportTypeOptions.getLoading(), () => transportTypes.value],
  ([loading]) => {
    if (loading) return;
    const rows = transportTypes.value ?? [];
    const completed = withDefaultRow<WizardProjectType["transportTypes"][number]>(rows, generateDefaultTransportType);
    if (completed !== rows) transportTypes.value = completed;
  },
  { immediate: true }
);
</script>

<template>
  <!-- Template -->
  <LoadingWrapper :loading="transportTypeOptions.getLoading()">
    <div class="w-full">
      <StepIntro
        class="mb-6"
        heading="How far do materials travel?"
        hint="A4, transport. Distances and vehicle types; the defaults come from the country's data." />

      <div
        v-if="BR2025"
        class="bg-slate-0 rounded-8 mt-2 flex h-[300px] flex-col items-center gap-2 border border-slate-300 p-5">
        <h4 class="t-sh2 text-type-default pt-[50px] pb-3 text-center">
          A new transport configuration <br />
          feature is coming soon
        </h4>
        <p class="t-body text-type-default text-center">
          We're working on a tailored setup for this calculation type,<br />
          with support for project-level pre-configuration.
        </p>
      </div>
      <div
        v-else
        class="rounded-8 bg-slate-0 p-px-20 flex w-full flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <Truck
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Transport types</CardHeading>
          </div>
        </div>
        <!-- A native disabled fieldset makes every control inside inert while a revision's saved rows
             are on screen — the type select, both number inputs, the name field and the lock button
             alike. Nothing to thread a `disabled` prop through, and it stays keyboard-correct. The
             rows also take `read-only`, because their greyed-out styling is prop-keyed: without it
             the fields would be inert but still look editable. -->
        <fieldset
          class="flex flex-col gap-2"
          :disabled="isReadOnly">
          <!-- Default -->
          <TransportRow
            v-for="transportType in defaultTransportTypes"
            :key="transportType.id"
            type="default"
            :read-only="isReadOnly"
            :was-validated="wasValidated && !isReadOnly"
            transport-label="Default transport type"
            :transport-type-options="transportTypeOptions.getTransportTypeOptions()"
            :item="transportType" />

          <!-- Generic  -->
          <TransportRow
            v-for="transport in genericTransportTypes"
            :key="transport.id"
            :allow-delete="allowDelete(transport.id)"
            :read-only="isReadOnly"
            :was-validated="wasValidated && !isReadOnly"
            :transport-type-options="transportTypeOptions.getTransportTypeOptions()"
            :item="transport"
            @handle-remove-transport="handleRemoveTransport" />

          <!-- A save point taken before anyone opened this section holds no rows, and that is what it
               has to say. Nothing is synthesised into it: the Default row is minted for the WORKING
               list only. -->
          <p
            v-if="isReadOnly && displayedTransportTypes.length === 0"
            class="t-body text-type-muted py-2 text-center">
            This revision has no transport types.
          </p>

          <!-- Generic  Add. Hidden rather than disabled while previewing: a dead button with no
               explanation is worse than none, and the banner above already says the view is read-only. -->
          <button
            v-if="!isReadOnly"
            data-cy="transport-add--btn"
            class="rounded-8 bg-slate-0/30 hover:bg-slate-0 focus-ring flex h-full w-full cursor-pointer items-center justify-center gap-2 border border-slate-200 p-4 font-bold shadow-xs transition-all"
            @click="handleAddTransport">
            <Plus
              class="icon-md"
              :stroke-width="1.5"
              aria-hidden="true" /><span>Add transport type</span>
          </button>
        </fieldset>
      </div>
    </div>
  </LoadingWrapper>
</template>
